import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import {
  FiCheckCircle,
  FiEye,
  FiFileText,
  FiSave,
  FiShield,
  FiTrash2,
  FiX,
  FiXCircle,
} from 'react-icons/fi';
import { API_URL } from '../../Api/api';
import { getAuthToken } from '../../Api/authToken';
import './AdminPages.css';
import './UserKycManagement.css';

const STATUS_META = {
  pending: { label: 'Pending', cls: 'ukyc-status--pending' },
  approved: { label: 'Approved', cls: 'ukyc-status--approved' },
  rejected: { label: 'Rejected', cls: 'ukyc-status--rejected' },
};

const guessMime = (filename = '') => {
  const ext = String(filename).split('.').pop()?.toLowerCase();
  if (['jpg', 'jpeg'].includes(ext)) return 'image/jpeg';
  if (ext === 'png') return 'image/png';
  if (ext === 'webp') return 'image/webp';
  if (ext === 'gif') return 'image/gif';
  if (ext === 'pdf') return 'application/pdf';
  return 'application/octet-stream';
};

const isImageMime = (mime) => mime.startsWith('image/');
const isPdfMime = (mime) => mime === 'application/pdf';

const UserKycManagement = () => {
  const [tab, setTab] = useState('config');
  const [loading, setLoading] = useState(true);
  const [savingRole, setSavingRole] = useState('');
  const [roles, setRoles] = useState([]);
  const [docTypes, setDocTypes] = useState([]);
  const [requests, setRequests] = useState([]);
  const [requestsLoading, setRequestsLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');

  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);

  const previewUrlRef = useRef(null);

  const authHeaders = { Authorization: `Bearer ${getAuthToken()}` };

  const loadConfig = useCallback(async () => {
    const res = await axios.get(`${API_URL}/admin/user-kyc/settings`, { headers: authHeaders });
    setRoles(Array.isArray(res.data?.roles) ? res.data.roles : []);
    setDocTypes(Array.isArray(res.data?.document_types) ? res.data.document_types : []);
  }, [authHeaders.Authorization]);

  const loadRequests = useCallback(async () => {
    setRequestsLoading(true);
    try {
      const params = {};
      if (statusFilter) params.status = statusFilter;
      const res = await axios.get(`${API_URL}/admin/user-kyc/requests`, { headers: authHeaders, params });
      setRequests(Array.isArray(res.data) ? res.data : []);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load KYC requests');
    } finally {
      setRequestsLoading(false);
    }
  }, [authHeaders.Authorization, statusFilter]);

  useEffect(() => {
    (async () => {
      try {
        await loadConfig();
      } catch (error) {
        toast.error(error.response?.data?.error || 'Failed to load User KYC settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [loadConfig]);

  useEffect(() => {
    if (tab === 'requests') loadRequests();
  }, [tab, loadRequests]);

  useEffect(() => () => {
    if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
  }, []);

  const closePreview = () => {
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreview(null);
    setPreviewLoading(false);
  };

  const openPreview = async (requestId, doc) => {
    setPreviewLoading(true);
    setPreview({ requestId, doc, blobUrl: null, mimeType: guessMime(doc.document_file) });
    try {
      const res = await axios.get(
        `${API_URL}/admin/user-kyc/requests/${requestId}/documents/${doc.document_type_id}`,
        { headers: authHeaders, responseType: 'blob' },
      );
      const mimeType = res.headers['content-type']?.split(';')[0]?.trim()
        || guessMime(doc.document_file);
      if (previewUrlRef.current) URL.revokeObjectURL(previewUrlRef.current);
      const blobUrl = URL.createObjectURL(new Blob([res.data], { type: mimeType }));
      previewUrlRef.current = blobUrl;
      setPreview({ requestId, doc, blobUrl, mimeType });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to load document preview');
      closePreview();
    } finally {
      setPreviewLoading(false);
    }
  };

  const confirmDeleteDocument = async () => {
    if (!deleteTarget) return;
    const { requestId, doc } = deleteTarget;
    setDeleteLoading(true);
    try {
      const res = await axios.delete(
        `${API_URL}/admin/user-kyc/requests/${requestId}/documents/${doc.document_type_id}`,
        { headers: authHeaders },
      );
      setRequests((prev) => prev.map((r) => (r.id === requestId ? res.data : r)));
      if (
        preview?.requestId === requestId
        && preview?.doc?.document_type_id === doc.document_type_id
      ) {
        closePreview();
      }
      toast.success('Document deleted successfully');
      setDeleteTarget(null);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete document');
    } finally {
      setDeleteLoading(false);
    }
  };

  const updateRoleField = (roleId, patch) => {
    setRoles((prev) => prev.map((r) => (r.role_id === roleId ? { ...r, ...patch } : r)));
  };

  const toggleDocForRole = (roleId, docId) => {
    setRoles((prev) => prev.map((r) => {
      if (r.role_id !== roleId) return r;
      const ids = new Set(r.document_type_ids || []);
      if (ids.has(docId)) ids.delete(docId);
      else ids.add(docId);
      return { ...r, document_type_ids: [...ids] };
    }));
  };

  const saveRole = async (role) => {
    setSavingRole(role.role_id);
    try {
      await axios.put(`${API_URL}/admin/user-kyc/settings/${role.role_id}`, {
        kyc_required: Boolean(role.kyc_required),
        role_label: role.role_label,
        document_type_ids: role.document_type_ids || [],
      }, { headers: authHeaders });
      toast.success(`Saved settings for ${role.role_label || role.role_id}`);
      await loadConfig();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to save role settings');
    } finally {
      setSavingRole('');
    }
  };

  const reviewRequest = async (id, status) => {
    const admin_note = status === 'rejected'
      ? window.prompt('Rejection reason (optional):', '') || ''
      : '';
    try {
      await axios.put(`${API_URL}/admin/user-kyc/requests/${id}/status`, { status, admin_note }, { headers: authHeaders });
      toast.success(`KYC ${status}`);
      loadRequests();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to update request');
    }
  };

  if (loading) {
    return (
      <div className="admin-page">
        <div className="page-header"><h1>User KYC</h1></div>
        <div className="admin-card skeleton" style={{ height: 280, borderRadius: 8 }} />
      </div>
    );
  }

  return (
    <div className="admin-page ukyc-page">
      <div className="page-header">
        <div>
          <h1><FiShield style={{ verticalAlign: 'middle', marginRight: 8 }} />User KYC</h1>
          <p>Configure KYC requirements per listing role and review user submissions.</p>
        </div>
      </div>

      <div className="ukyc-tabs">
        <button type="button" className={tab === 'config' ? 'ukyc-tab ukyc-tab--active' : 'ukyc-tab'} onClick={() => setTab('config')}>
          Role Configuration
        </button>
        <button type="button" className={tab === 'requests' ? 'ukyc-tab ukyc-tab--active' : 'ukyc-tab'} onClick={() => setTab('requests')}>
          Submissions
        </button>
      </div>

      {tab === 'config' && (
        <div className="ukyc-config-grid">
          {roles.map((role) => (
            <div key={role.role_id} className="admin-card ukyc-role-card">
              <div className="ukyc-role-head">
                <div>
                  <h3>{role.role_label || role.role_id}</h3>
                  <p className="ukyc-role-id">Role ID: {role.role_id}</p>
                </div>
                <label className="ukyc-toggle">
                  <input
                    type="checkbox"
                    checked={Boolean(role.kyc_required)}
                    onChange={(e) => updateRoleField(role.role_id, { kyc_required: e.target.checked })}
                  />
                  <span>KYC Required</span>
                </label>
              </div>

              {role.kyc_required && (
                <div className="ukyc-docs">
                  <p className="ukyc-docs-label">Required documents</p>
                  {docTypes.length === 0 ? (
                    <p className="ukyc-hint">No document types yet. Add them under KYC Document Types.</p>
                  ) : (
                    <div className="ukyc-doc-list">
                      {docTypes.map((doc) => (
                        <label key={doc.id} className="ukyc-doc-item">
                          <input
                            type="checkbox"
                            checked={(role.document_type_ids || []).includes(doc.id)}
                            onChange={() => toggleDocForRole(role.role_id, doc.id)}
                          />
                          <span>{doc.name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </div>
              )}

              <button
                type="button"
                className="btn-primary ukyc-save-btn"
                disabled={savingRole === role.role_id}
                onClick={() => saveRole(role)}
              >
                <FiSave /> {savingRole === role.role_id ? 'Saving…' : 'Save Settings'}
              </button>
            </div>
          ))}
        </div>
      )}

      {tab === 'requests' && (
        <div className="admin-card">
          <div className="ukyc-requests-toolbar">
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">All statuses</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
            </select>
          </div>

          {requestsLoading ? (
            <div className="skeleton" style={{ height: 200, borderRadius: 8 }} />
          ) : requests.length === 0 ? (
            <p className="ukyc-empty">No user KYC submissions yet.</p>
          ) : (
            <div className="ukyc-requests">
              {requests.map((req) => {
                const meta = STATUS_META[req.status] || STATUS_META.pending;
                return (
                  <article key={req.id} className="ukyc-request-card">
                    <div className="ukyc-request-head">
                      <div>
                        <strong>{req.user_name || req.user_email}</strong>
                        <p>{req.user_email} · Role: {req.listing_role}</p>
                        <p className="ukyc-request-date">{new Date(req.createdAt).toLocaleString()}</p>
                      </div>
                      <span className={`ukyc-status ${meta.cls}`}>{meta.label}</span>
                    </div>

                    <div className="ukyc-request-docs-wrap">
                      <p className="ukyc-docs-label">Uploaded documents</p>
                      {(req.documents || []).length === 0 ? (
                        <p className="ukyc-hint">No documents attached.</p>
                      ) : (
                        <ul className="ukyc-request-docs">
                          {(req.documents || []).map((d, i) => (
                            <li key={`${d.document_type_id}-${i}`} className="ukyc-doc-row">
                              <div className="ukyc-doc-row__info">
                                <FiFileText className="ukyc-doc-row__icon" aria-hidden />
                                <div>
                                  <strong>{d.document_type_name}</strong>
                                  <span className="ukyc-doc-row__file">{d.document_file}</span>
                                </div>
                              </div>
                              <div className="ukyc-doc-row__actions">
                                <button
                                  type="button"
                                  className="btn-secondary ukyc-doc-btn"
                                  onClick={() => openPreview(req.id, d)}
                                >
                                  <FiEye /> Preview
                                </button>
                                <button
                                  type="button"
                                  className="btn-secondary ukyc-doc-btn ukyc-doc-btn--danger"
                                  onClick={() => setDeleteTarget({ requestId: req.id, doc: d, req })}
                                >
                                  <FiTrash2 /> Delete
                                </button>
                              </div>
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>

                    {req.status === 'pending' && (
                      <div className="ukyc-request-actions">
                        <button type="button" className="btn-primary" onClick={() => reviewRequest(req.id, 'approved')}>
                          <FiCheckCircle /> Approve
                        </button>
                        <button type="button" className="btn-secondary" onClick={() => reviewRequest(req.id, 'rejected')}>
                          <FiXCircle /> Reject
                        </button>
                      </div>
                    )}
                    {req.admin_note && <p className="ukyc-admin-note">Note: {req.admin_note}</p>}
                  </article>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* Document preview modal */}
      {preview && (
        <div className="admin-modal-overlay animate-fade-in ukyc-preview-overlay" onClick={closePreview}>
          <div
            className="admin-modal ukyc-preview-modal"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="ukyc-preview-title"
          >
            <div className="modal-header">
              <div>
                <h2 id="ukyc-preview-title">{preview.doc?.document_type_name || 'Document Preview'}</h2>
                <p className="ukyc-preview-sub">{preview.doc?.document_file}</p>
              </div>
              <button type="button" className="btn-icon" onClick={closePreview} aria-label="Close preview">
                <FiX />
              </button>
            </div>

            <div className="ukyc-preview-body">
              {previewLoading ? (
                <div className="ukyc-preview-loading">
                  <div className="skeleton" style={{ height: 320, borderRadius: 8 }} />
                  <p>Loading document…</p>
                </div>
              ) : preview.blobUrl && isImageMime(preview.mimeType) ? (
                <img
                  src={preview.blobUrl}
                  alt={preview.doc?.document_type_name || 'KYC document'}
                  className="ukyc-preview-image"
                />
              ) : preview.blobUrl && isPdfMime(preview.mimeType) ? (
                <iframe
                  title={preview.doc?.document_type_name || 'PDF preview'}
                  src={preview.blobUrl}
                  className="ukyc-preview-pdf"
                />
              ) : preview.blobUrl ? (
                <div className="ukyc-preview-fallback">
                  <FiFileText size={40} />
                  <p>Preview is not available for this file type.</p>
                  <a
                    href={preview.blobUrl}
                    download={preview.doc?.document_file || 'document'}
                    className="btn-primary"
                  >
                    Download file
                  </a>
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Delete confirmation modal */}
      {deleteTarget && (
        <div className="admin-modal-overlay animate-fade-in" onClick={() => !deleteLoading && setDeleteTarget(null)}>
          <div className="admin-modal ukyc-delete-modal" onClick={(e) => e.stopPropagation()} role="dialog" aria-modal="true">
            <div className="modal-header">
              <h2>Delete Document</h2>
              <button
                type="button"
                className="btn-icon"
                onClick={() => !deleteLoading && setDeleteTarget(null)}
                aria-label="Close"
                disabled={deleteLoading}
              >
                <FiX />
              </button>
            </div>
            <div className="modal-form">
              <p className="ukyc-delete-msg">
                Are you sure you want to delete <strong>{deleteTarget.doc.document_type_name}</strong>?
                This will permanently remove the file from storage and cannot be undone.
              </p>
              {deleteTarget.doc.document_file && (
                <p className="ukyc-delete-file">{deleteTarget.doc.document_file}</p>
              )}
              <div className="ukyc-delete-actions">
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => setDeleteTarget(null)}
                  disabled={deleteLoading}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn-danger"
                  onClick={confirmDeleteDocument}
                  disabled={deleteLoading}
                >
                  <FiTrash2 /> {deleteLoading ? 'Deleting…' : 'Delete Document'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserKycManagement;
